<script setup>
import {
  onMounted,
  provide,
} from 'vue';

import PrintablePowerGrid from '@/views/character-view-printable/sections/PrintablePowerGrid.vue';
import PrintableExtendedPowerGrid from '@/views/character-view-printable/sections/PrintableExtendedPowerGrid.vue';
import PrintableSpecialAttributesSection from '@/views/character-view-printable/sections/PrintableSpecialAttributesSection.vue';
import PrintableCharacterList from '@/views/character-view-printable/sections/PrintableCharacterList.vue';
import PrintableHeaderAQTwo from '@/views/character-view-printable/sections/PrintableHeaderAQTwo.vue';
import CharacterObjectFieldWithCalculations from '@/components/CharacterObjectFieldWithCalculations.vue';

const quantifiables = [
  '66314b12bd0e0b010a692e34',
  '66314c34bd0e0b010a692e6a',
  '66314e11bd0e0b010a692ef5',
  '66314fadbd0e0b010a692f3b',
  '66315349bd0e0b010a6932c5',
];

const props = defineProps({
  character: {
    type: Object,
    required: true,
  },
  system: {
    type: Object,
    required: true,
  },
});

provide('printCharacter', props.character);
provide('printSystem', props.system);

const getEquipmentItems = () => {
  const items = [];
  const listDef = props.system.listDefinitions.find((l) => l.listName.toLowerCase() === 'equipment');
  if (listDef) {
    const charList = props.character.characterLists.find((l) => l.listDefinition === listDef._id);
    if (charList) {
      items.push(...charList.items);
    }
  }
  return items;
};

const getObjectParenthetical = (item) => {
  let parenthetical = item.objectDefinition.objectName;
  switch (item.objectDefinition._id) {
    case '6631914fbd0e0b010a695cb1':
      parenthetical = item?.fields?.appareltype;
      break;
    case '66296bf3bd0e0b010a68e6ca':
      parenthetical = item?.fields?.kit;
      break;
    case '662c0d95bd0e0b010a69169b':
    case '662c0e94bd0e0b010a691a50':
      parenthetical = item?.fields?.weapontype;
      break;
    case '662c103bbd0e0b010a691c2f':
      parenthetical = `Armor: ${item?.fields?.armorrank}`;
      break;
    case '66314b12bd0e0b010a692e34':
      parenthetical = item?.fields?.herbaltype;
      break;
    case '66314c34bd0e0b010a692e6a':
      parenthetical = item?.fields?.poisontype;
      break;
    case '66314e11bd0e0b010a692ef5':
      parenthetical = item?.fields?.chemicaltype;
      break;
    case '66314fadbd0e0b010a692f3b':
      parenthetical = item?.fields?.potiontype;
      break;
    case '66315349bd0e0b010a6932c5':
      parenthetical = item?.fields?.concoctiontype;
      break;
    case '6631833ebd0e0b010a6949ff':
      parenthetical = item?.fields?.traporlocktype;
      break;
    case '6631836cbd0e0b010a694a34':
      parenthetical = item?.fields?.gadgettype;
      break;
    case '663187cbbd0e0b010a694e61':
      parenthetical = item?.fields?.vortometrytype;
      break;
    case '66318b7ebd0e0b010a6955b6':
      parenthetical = item?.fields?.artifacttype;
      break;
    case '663197d3bd0e0b010a696785':
      parenthetical = item?.fields?.spelltier;
      break;
    case '662c09b4bd0e0b010a69131d':
    case '662c098ebd0e0b010a6912e8':
    case '662c09c8bd0e0b010a691350':
      parenthetical = item?.objectDefinition?.objectName;
      break;
    default:
      parenthetical = '';
  }
  return parenthetical;
};

const isQuantifiable = (item) => quantifiables.includes(item.objectDefinition._id);

const getObjectQuantity = (item) => {
  let quantity = '';
  if (isQuantifiable(item)) {
    quantity = item?.fields?.quantity;
  }
  return quantity;
};

onMounted(() => {
  // eslint-disable-next-line vue/no-mutating-props
  props.character.equipment = getEquipmentItems();
});
</script>

<template>
  <div class="char-sheet no-anything">
    <div class="page page-1 no-anything">
      <printable-header-a-q-two />
      <div class="page-body w3-row">
        <div class="w3-col s8">
          <div class="w3-row">
            <printable-power-grid />
          </div>
          <div class="w3-row">
            <printable-extended-power-grid />
          </div>
          <div class="w3-row">
            <printable-character-list title="Relationships" character-list-id="665cf81f46e9d7380142e494">
              <template #listItem="{ item }">
                <div class="name-label">
                  <span class="label">{{ item.label }}</span> <span class="parenthetical">({{ item.fields['relationshiptype'] }})</span> - <span class="description">{{ item.description }}</span>
                </div>
              </template>
            </printable-character-list>
          </div>
        </div>
        <div class="w3-col s4 full-height" style="border: 1px solid lightgrey;border-top: 0;">
          <div class="w3-row">
            <printable-special-attributes-section />
          </div>
          <div class="w3-row">
            <div class="notes-section">
              <div class="notes-title">
                Notes
              </div>
              <div class="notes-box" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="page page-other">
      <div class="w3-row">
        <printable-character-list title="Equipment" character-list-id="66296ae3bd0e0b010a68e631" sort-field-name="value" sort-descending>
          <template #listItem="{ item }">
            <div class="equipment-item">
              <div class="equipment-fixed-subsection">
                <div class="equipment-label">
                  {{ item.label }} <span class="parenthetical">({{ getObjectParenthetical(item) }}<span v-if="isQuantifiable(item)"> x{{ getObjectQuantity(item) }}</span>)&nbsp;</span>
                </div>
                <div class="equipment-description">
                  - {{ item.description }}
                </div>
                <div class="equipment-value">
                  <character-object-field-with-calculations v-if="item?.fields?.value" field-name="value" :character-object="item" field-label="" show-only-calculated hide-info-button />
                </div>
              </div>
              <div v-if="item.modifications && item.modifications.length" class="equipment-mods-subsection">
                <div v-for="mod in item.modifications" :key="mod._id" class="mod">
                  <b>{{ mod.modification.modificationName }}</b> - {{ mod.modification.description }}
                </div>
              </div>
            </div>
          </template>
        </printable-character-list>
      </div>
      <div class="w3-row">
        <div class="w3-col s6">
          <printable-character-list title="Pets" character-list-id="66296af6bd0e0b010a68e664">
            <template #listItem="{ item }">
              <span class="label">{{ item.label }}</span> - <span class="description">{{ item.description }}</span>
              <div class="equipment-value">
                <character-object-field-with-calculations v-if="item?.fields?.value" field-name="value" :character-object="item" field-label="" show-only-calculated hide-info-button />
              </div>
              <div v-if="item.modifications && item.modifications.length" class="equipment-mods-subsection">
                <div v-for="mod in item.modifications" :key="mod._id" class="mod">
                  <b>{{ mod.modification.modificationName }}</b> - {{ mod.modification.description }}
                </div>
              </div>
            </template>
          </printable-character-list>
        </div>
        <div class="w3-col s6">
          <printable-character-list title="Accomplishments" character-list-id="66296b1cbd0e0b010a68e697" sort-field-name="displaypriority" sort-descending filter-field-name="displaypriority" :filter-field-min-value="1">
            <template #listItem="{ item }">
              <div class="equipment-item">
                <div class="equipment-fixed-subsection">
                  <div class="equipment-label">
                    {{ item.label }} <span class="parenthetical">({{ getObjectParenthetical(item) }}<span v-if="isQuantifiable(item)"> x{{ getObjectQuantity(item) }}</span>)&nbsp;</span>
                  </div>
                  <div class="equipment-description">
                    - {{ item.description }}
                  </div>
                </div>
              </div>
            </template>
          </printable-character-list>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "@/views/character-view-printable/styles/layout.css";

.char-sheet {
  font-size: 11px;
}

.page {
  width: 8in;
  height: 10.5in;
  vertical-align: top;
}

.page-1 {
  margin: 0;
  padding: 0;
}

.page-other {
  page-break-before: always;
}

.page-1 .page-body {
  width: 100%;
  height: calc(100% - 1in);
}

/* Per-item classes */
.item-value {
  width: 0.5in;
  float: right;
  display: inline-block;
  vertical-align: top;
  font-size: 0.15in;
  overflow: hidden;
  text-overflow: ellipsis;
  border: 1px solid lightgrey;
  border-radius: 3px;
  padding: 2px;
}

.mod {
  padding-left: 20px;
  white-space: nowrap;
  display: inline-block;
  overflow: hidden;
  width: 50%;
  text-overflow: ellipsis;
}

.mod::before {
  font-size: 0.2in;
  line-height: 0.2in;
  content: "+";
  margin-right: 8px;
  color: black;
}

.equipment-item {
  width: 100%;
  display: inline-block;
  vertical-align: top;
}

.equipment-fixed-subsection {
  max-height: 0.3in;
  overflow: hidden;
  text-overflow: ellipsis;
}

.equipment-label {
  font-size: 0.8rem;
  font-weight: bold;
  display: inline-block;
}

.equipment-description {
  display: inline-block;
  font-size: 0.11in;
}

.equipment-mods-subsection {
  display: inline-block;
  width: 100%;
  font-size: 0.11in;
}

.equipment-value {
  display: inline-block;
  float: right;
  vertical-align: top;
  font-size: 0.15in;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 5px;
}

/* These are also defined as duplicates in the child PrintableCharacterList because of style scoping. */
.name-label {
  display: inline-block;
  font-size: 0.8rem;
  width: calc(100% - 0.5in);
}

.label {
  font-weight: bold;
}

.parenthetical {
}

.description {
}

.notes-section {
  width: 100%;
}

.notes-box {
  display: inline-block;
  vertical-align: top;
}

.notes-title {
  background-color: black;
  color: white;
  text-align: center;
}
</style>
